import React, { useCallback, useMemo } from 'react';
import type { EntryPointProps } from 'react-relay';
import { GIRA_APOLLO_CLIENT } from '@atlassian/jira-apollo-multiple-clients/src/common/constants.tsx';
import {
	ApolloClientProvider,
	resetCache,
} from '@atlassian/jira-apollo-multiple-clients/src/main.tsx';
import { JiraIssueCreateErrorBoundary } from '@atlassian/jira-issue-create-analytics-common/src/ui/jira-issue-create-error-boundary/index.tsx';
import { usePublishFormContext } from '@atlassian/jira-issue-create-form-context/src/utils.tsx';
import { useIssueRestoreMetaService } from '@atlassian/jira-issue-create-restore/src/services/issue-restore-meta/index.tsx';
import { useTriggerIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/main.tsx';
import { getAdditionalAttributes } from '@atlassian/jira-issue-create-analytics-common/src/services/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import messages from './common/messages.tsx';
import type { Payload } from './common/types/index.tsx';
import { NavigationDiscardChangesContainer } from './controllers/navigation-discard-changes/index.tsx';
import { useIssueCreateFromAnywhereContext } from './controllers/use-issue-create-from-anywhere-context/index.tsx';
import { usePublishUseTriggerIssueCreateModalChange } from './controllers/use-publish-use-trigger-issue-create-modal-change/index.tsx';
import { IssueRestoreWrapper } from './ui/issue-restore-wrapper/index.tsx';
import IssueCreateModal from './ui/modal/index.tsx';
import type { CreateModalProps } from './ui/modal/types.tsx';

type IssueCreateAppWithApolloProviderProps = {
	onError?: () => void;
	shouldForceCheckIsModalOpen?: boolean;
};

const onResetApolloCache = () => resetCache(GIRA_APOLLO_CLIENT);

const IssueCreateAppWithApolloProvider = ({
	onError,
	shouldForceCheckIsModalOpen = false,
}: IssueCreateAppWithApolloProviderProps) => {
	const [{ isModalOpen }] = useTriggerIssueCreateModal();
	const { deleteIssueRestoreMeta } = useIssueRestoreMetaService();

	const _onError = useCallback(() => {
		onError?.();
		deleteIssueRestoreMeta();
	}, [onError, deleteIssueRestoreMeta]);

	const { giraClient } = useIssueCreateFromAnywhereContext();

	usePublishUseTriggerIssueCreateModalChange();

	usePublishFormContext();

	const renderIssueCreateModal = ({
		onTransformCreateFormProps,
		setShouldRestore,
	}: CreateModalProps = {}) => {
		if (isModalOpen) {
			return (
				<IssueCreateModal
					onTransformCreateFormProps={onTransformCreateFormProps}
					setShouldRestore={setShouldRestore}
					onResetApolloCache={onResetApolloCache}
				/>
			);
		}
		return null;
	};
	const attributes = useMemo(
		() => ({
			isModalOpen,
			shouldForceCheckIsModalOpen,
		}),
		[isModalOpen, shouldForceCheckIsModalOpen],
	);
	if (
		// don't render IssueCreate unless the modal is open
		// this fixes problems with loading the correct data for pre-selected sites
		// this is a hot fix to prevent the modal from not opening when it should (you read that right, see HOT-113496)
		shouldForceCheckIsModalOpen &&
		!isModalOpen
	) {
		return;
	}

	return (
		<JiraIssueCreateErrorBoundary
			id="issue-create-app"
			packageName="jiraIssueCreate"
			fallback="flag"
			onError={_onError}
			sendToPrivacyUnsafeSplunk
			attributes={() => ({
				...getAdditionalAttributes(),
				...(fg('jira-issue-create-error-boundary-enabled') ? attributes : {}),
			})}
		>
			<ApolloClientProvider client={giraClient}>
				<NavigationDiscardChangesContainer>
					<IssueRestoreWrapper>
						{({ onTransformCreateFormProps, setShouldRestore }) => (
							<JiraIssueCreateErrorBoundary
								id="issue-create-app-children"
								packageName="jiraIssueCreate"
								fallback="flag"
								onError={_onError}
								sendToPrivacyUnsafeSplunk
								attributes={() => ({
									...getAdditionalAttributes(),
									...(fg('jira-issue-create-error-boundary-enabled') ? attributes : {}),
								})}
							>
								{renderIssueCreateModal({
									onTransformCreateFormProps,
									setShouldRestore,
								})}
							</JiraIssueCreateErrorBoundary>
						)}
					</IssueRestoreWrapper>
				</NavigationDiscardChangesContainer>
			</ApolloClientProvider>
		</JiraIssueCreateErrorBoundary>
	);
};

// wrapper component is used since createEntryPoint cannot infer type from default export: https://atlassian.slack.com/archives/C02785CLSTW/p1737066610473829?thread_ts=1736986489.254059&cid=C02785CLSTW
export const IssueCreateAppWithApolloProviderEntrypoint = ({
	props,
}: EntryPointProps<{}, {}, IssueCreateAppWithApolloProviderProps, {}>) => {
	return <IssueCreateAppWithApolloProvider {...props} />;
};

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export type { Payload };

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports, @atlassian/eng-health/no-barrel-files/disallow-reexports, @atlassian/eng-health/no-barrel-files/disallow-reexports
export { ApolloClientProvider, IssueCreateModal, messages };

export default IssueCreateAppWithApolloProvider;
